import { useTranslation } from "react-i18next";
import BaseButton from "@/components/common/baseButton";
import BaseInput from "@/components/common/baseInput";
import { useNavigate } from "react-router-dom";
import "./import.css";
import { useEffect, useState } from "react";
import popupService from "@/services/popupService";
import { Employee } from "@/models/employees";
import { EmployeeGroup } from "@/models/apiModels";
import api from "@/services/api";
import {
    Dictionary,
    GetTaskInfoResponse,
    GetTaskProgressRequest,
    GetTaskProgressResponse,
    NewGroupRequest,
    ProgressStopCode,
    QueueJobStatus,
    SaveProfileRequest,
    ValidateImportRequest,
} from "@/models/import";
import { IconButton, InputAdornment, MenuItem, Modal, Popover, styled, Typography } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import swal, { SweetAlertOptions } from "sweetalert2";
import { AxiosResponse } from "axios";
import authStore from "@/stores/authStore";
import { ReactComponent as InfoIcon } from "@/assets/icons/info-icon.svg";

class SelectOptionModel {
    id: number = 0;
    text: string = "";
    alias: string = "";
}
const selectOptions: SelectOptionModel[] = [
    {
        id: 1,
        text: "Personal ID Number",
        alias: "PersonalIdNumber",
    },
    {
        id: 2,
        text: "First Name",
        alias: "FirstName",
    },
    {
        id: 3,
        text: "Last Name",
        alias: "LastName",
    },
    {
        id: 4,
        text: "Organization Unit",
        alias: "OrganizationUnit",
    },
    {
        id: 5,
        text: "Employee Number",
        alias: "EmployeeNumber",
    },
    {
        id: 6,
        text: "Password",
        alias: "Password",
    },
    {
        id: 7,
        text: "Mobile Phone",
        alias: "MobilePhone",
    },
    {
        id: 8,
        text: "Work Phone",
        alias: "WorkPhone",
    },
    {
        id: 9,
        text: "Email",
        alias: "Email",
    },
    {
        id: 10,
        text: "Start Date dd/mm/yyyy",
        alias: "WorkStartDate",
    },
    {
        id: 11,
        text: "End Date dd/mm/yyyy",
        alias: "WorkEndDate",
    },
    {
        id: 12,
        text: "Gender",
        alias: "Gender",
    },
    {
        id: 13,
        text: "Start Date mm/dd/yyyy",
        alias: "WorkStartDateOpt",
    },
    {
        id: 14,
        text: "End Date mm/dd/yyyy",
        alias: "WorkEndDateOpt",
    },
    {
        id: 15,
        text: "Username",
        alias: "Username",
    },
    {
        id: 16,
        text: "Employee Group",
        alias: "EmployeeGroup",
    },
    {
        id: 17,
        text: "Employee Status",
        alias: "EmployeeStatus",
    },
];
const ImportEmployees = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [fieldsTypes, setFieldsTypes] = useState<SelectOptionModel[]>(selectOptions);
    const [fileSheets, setFileSheets] = useState<string[]>(["sheet1", "sheet2"]);
    const [parsedFields, setParsedFields] = useState<any[]>([]);
    const [employeeGroups, setEmployeeGroups] = useState<EmployeeGroup[]>([]);
    const [taskId, setTaskId] = useState("");
    const [mapping, setMapping] = useState<Dictionary<string>>({});
    const [lockedMapping, setLockedMapping] = useState<boolean>(false);
    const [isOriginSelectedProfileMapping, setIsOriginSelectedProfileMapping] = useState(false);
    const [mappingFinished, setMappingFinished] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState("");
    const [openAddNewGroup, setOpenAddNewGroup] = useState(false);
    const [newGroupName, setNewGroupName] = useState("");
    const [newGroupId, setNewGroupId] = useState("");
    const [nextBtnActive, setNextBtnActive] = useState(false);
    const [uniqueColumnValue, setUniqueColumnValue] = useState<string | null>("EmployeeNumber");

    const uniqueKeyColumnTypes = ["EmployeeNumber", "PersonalNumber"];

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            if (currentStep === 1) {
                if (uploadedFile) {
                    setNextBtnActive(true);
                    return;
                } else {
                    setNextBtnActive(false);
                    return;
                }
            } else if (currentStep === 2) {
                if (!mapping) {
                    setNextBtnActive(false);
                    return;
                }
                const result = Object.keys(mapping).map((key) => [key, mapping[key]]);
                if (result.length < 2) {
                    setNextBtnActive(false);
                    return;
                }

                let foundUniqueField: SelectOptionModel | undefined = undefined;
                const uniqueEmployeeNumber = Object.values(mapping).includes("5");
                const hasUniqueValues = ["1", "5"].some((value) => Object.values(mapping).includes(value));

                if (hasUniqueValues) {
                    if (uniqueEmployeeNumber) {
                        setUniqueColumnValue("EmployeeNumber");
                        foundUniqueField = fieldsTypes.find((x) => x.text === "EmployeeNumber" || x.id === 5);
                    } else {
                        setUniqueColumnValue("PersonalNumber");
                        foundUniqueField = fieldsTypes.find((x) => x.text === "PersonalNumber" || x.id === 1);
                    }
                }

                if (!foundUniqueField || !hasUniqueValues) {
                    setNextBtnActive(false);
                    return;
                } else {
                    setNextBtnActive(true);
                    return;
                }
            } else if (currentStep === 3) {
                if (!selectedGroupId) {
                    setNextBtnActive(false);
                    return;
                } else {
                    setNextBtnActive(true);
                    return;
                }
            }
        }
        return () => {
            ignore = true;
        };
    }, [currentStep, fieldsTypes, mapping, openAddNewGroup, selectedGroupId, uploadedFile]);

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            const uniqueEmployeeNumber = Object.values(mapping).includes("5");
            const uniquePersonalNumber = Object.values(mapping).includes("1");

            if (uniqueEmployeeNumber && uniqueColumnValue === "EmployeeNumber") {
                setNextBtnActive(true);
            } else if (uniquePersonalNumber && uniqueColumnValue === "PersonalNumber") {
                setNextBtnActive(true);
            } else {
                setNextBtnActive(false);
            }
        }

        return () => {
            ignore = true;
        };
    }, [uniqueColumnValue]);

    function selectGroup(groupId: string) {
        if (groupId === "-1") {
            setOpenAddNewGroup(true);
            setSelectedGroupId("");
        } else {
            setSelectedGroupId(groupId);
        }
    }
    useEffect(() => {
        let ignore = false;
        if (!openAddNewGroup) {
            setNewGroupName("");
            setNewGroupId("");
        }
        return () => {
            ignore = true;
        };
    }, [openAddNewGroup]);
    useEffect(() => {
        if (!parsedFields || parsedFields.length === 0) {
            return;
        }
        parsedFields.forEach((field, index) => {
            const foundField = fieldsTypes.find(
                (x) =>
                    x.alias.toLocaleLowerCase().replace(/ /g, "") ===
                    field[1]
                        .replace(/[^a-zA-Z ]/g, "")
                        .replace(/\s/g, "")
                        .toLocaleLowerCase()
            );
            if (foundField) {
                fullFillDictionary(index, foundField.id.toString());
            }
        });
    }, [parsedFields]);
    function renewImport() {
        setUploadedFile(null);
        setParsedFields([]);
        setMapping({});
        setSelectedGroupId("");
        setTaskId("");
    }
    function goToStartNewImport() {
        renewImport();
        setCurrentStep(1);
    }
    async function changeStep(step: number) {
        if (!uploadedFile && step >= 1) {
            return;
        }
        if (step === 0) {
            renewImport();
            setCurrentStep(1);
            return;
        }
        if (step >= 1) {
            if (currentStep === 3 && step === 2) {
                setMapping({});
                setCurrentStep(2);
                return;
            }
            if (currentStep === 2) {
                await startFrontendValidation();
                return;
            }
        }
        if (step === -1) {
            if (currentStep === 2 || currentStep === 3) {
                renewImport();
                setCurrentStep(1);
                return;
            }
            if (currentStep === 1) {
                navigate(`/employees`);
                return;
            }
        }
    }
    function startImportFile() {
        const inp = document.getElementById("uploadFile");
        if (!inp) {
            return;
        } else {
            inp.click();
        }
    }
    async function uploadeFile(e: any) {
        let file = e.target.files[0];
        if (!file) {
            return;
        }
        const fileExt = file.name.split(".").pop();
        setUploadedFile(file.name);
        const fileTypes: string[] = ["csv", "xls", "xlsx"];
        if (!fileTypes.includes(fileExt)) {
            swal.fire({
                icon: "error",
                text: t("employee.import.alert.incorrectFileType") as string,
            });
            setUploadedFile(null);
            return;
        }
        const fd = new FormData();
        fd.append("file", file, file.name);
        swal.showLoading(null);
        const response = await api.importUploadExcel(fd);
        if (response.data.success && response.data.taskId) {
            swal.close();
            await getImportFileUploadStatus(response.data.taskId);
            await setTaskId(response.data.taskId);
        }
    }
    //step 1
    async function getImportFileUploadStatus(idTask: string) {
        const req = new GetTaskProgressRequest();
        req.taskId = idTask;
        req.taskStatus = QueueJobStatus.Import_AmazonUpload_Progress;
        popupService.showLoading();
        const response = await api.queueImportProgress(req);
        if (response.data.error || response.data.stopMessage) {
            setUploadedFile(null);
            swal.fire({
                icon: "error",
                text: response.data.error ?? response.data.stopMessage,
            });
            return;
        }
        if (response.data.stopCode === ProgressStopCode.Success) {
            popupService.close();
            await getImportFileParseStatus(idTask);
            return;
        } else {
            setTimeout(async () => {
                await getImportFileUploadStatus(idTask);
            }, 1000);
        }
    }

    //step 2
    async function getImportFileParseStatus(idTask: string) {
        const req = new GetTaskProgressRequest();
        req.taskId = idTask;
        req.taskStatus = QueueJobStatus.Import_Parse_Progress;
        popupService.showLoading();
        const response = await api.queueImportProgress(req);
        if (response.data.error || response.data.stopMessage) {
            setUploadedFile(null);
            swal.fire({
                icon: "error",
                text: response.data.error ?? response.data.stopMessage,
            });
            return;
        }
        if (response.data.stopCode === ProgressStopCode.Success) {
            popupService.close();
            await getTaskInformation(idTask);
            return;
        } else {
            setTimeout(async () => {
                await getImportFileParseStatus(idTask);
            }, 1500);
        }
    }
    //step 6
    async function getImportValidationProgress(idTask: string) {
        const req = new GetTaskProgressRequest();
        req.taskId = idTask;
        req.taskStatus = QueueJobStatus.Import_Validate_Progress;
        popupService.showLoading();
        const response = await api.queueImportProgress(req);
        if (response.data.error || response.data.stopMessage) {
            swal.fire({
                icon: "error",
                text: response.data.error ?? response.data.stopMessage,
            });
            return;
        }
        if (response.data.stopCode === ProgressStopCode.None) {
            setTimeout(async () => {
                await getImportValidationProgress(idTask);
            }, 1500);
        } else {
            if (response.data.stopCode !== ProgressStopCode.Success) {
                popupService.close();
                await showImportResponse(response);
                return;
            } else if (response.data.stopCode === ProgressStopCode.Success) {
                var impStartApiResult = await api.importStart(taskId);
                if (impStartApiResult.data.errorMessage) {
                    swal.fire({
                        icon: "error",
                        text: impStartApiResult.data.errorMessage,
                    });
                    return;
                }
                await getImportedFileStatus(taskId);
                popupService.close();
                return;
            }
        }
    }
    //step 3
    async function getTaskInformation(idTask: string) {
        popupService.showLoading();
        const response = await api.getTaskInfo(idTask);
        if (response.data.error) {
            swal.fire({
                icon: "error",
                text: response.data.error,
            });
            return;
        } else if (response.data.status === QueueJobStatus.Import_Parse_Success) {
            popupService.close();
            setCurrentStep(2);
            const result = Object.keys(response.data.columnNames).map((key) => [key, response.data.columnNames[key]]);
            setParsedFields((oldArray) => [...oldArray, ...result]);
            return;
        } else {
            setTimeout(async () => {
                await getImportFileUploadStatus(idTask);
            }, 1000);
        }
    }

    //step 4
    async function startFrontendValidation() {
        if (!taskId) {
            return;
        }
        const result = Object.keys(mapping).map((key) => [key, mapping[key]]);
        if (result.length < 2) {
            swal.fire({
                icon: "error",
                text: t("employee.import.alert.atleast2") as string,
            });
            return;
        }

        let foundUniqueField: SelectOptionModel | undefined = undefined;
        const uniqueEmployeeNumber = Object.values(mapping).includes("5");
        const hasUniqueValues = ["1", "5"].some((value) => Object.values(mapping).includes(value));

        if (hasUniqueValues) {
            if (uniqueEmployeeNumber) {
                foundUniqueField = fieldsTypes.find((x) => x.text === "EmployeeNumber" || x.id === 5);
            } else {
                foundUniqueField = fieldsTypes.find((x) => x.text === "PersonalNumber" || x.id === 1);
            }
        }

        if (!foundUniqueField || !hasUniqueValues) {
            swal.fire({
                icon: "error",
                text: t("employee.import.alert.selectUniqueColumn") as string,
            });
            return;
        }
        if (result.length !== parsedFields.length) {
            const swalResult = await swal.fire({
                icon: "warning",
                text: t("employee.import.alert.notAllColumnMapped") as string,
                cancelButtonText: t("button.cancel") as string,
                showConfirmButton: true,
                showCancelButton: true,
                confirmButtonText: t("general.Continue") as string,
            });
            if (!swalResult.isConfirmed) {
                return;
            }
        }

        await startBackendValidation();
        return;
    }
    //step 5
    async function startBackendValidation() {
        if (authStore.employeeImportFrequencyValid) {
            await startBackendValidationReal();
        } else {
            var msg = t("employee.importFrequencyMsg");
            popupService.showConfirm(msg).then(async function (res) {
                if (res.isConfirmed) {
                    await startBackendValidationReal();
                }
            });
        }
    }
    async function startBackendValidationReal() {
        const request = new ValidateImportRequest();
        request.TaskId = taskId;
        request.Mapping = mapping!;
        request.UniqueKeyColumn = uniqueColumnValue ?? "EmployeeNumber";
        popupService.showLoading();
        const response = await api.importValidate(request);
        if (response.data.Error) {
            swal.fire({
                icon: "error",
                text: response.data.Error,
            });
            return;
        }
        popupService.close();
        await getImportValidationProgress(request.TaskId);
    }

    function fullFillDictionary(index: number, val: string) {
        const column = parsedFields[index][0];
        setMapping((prevData: any) => ({
            ...prevData,
            [column]: val.toString(),
        }));
    }
    function getFieldValue(index: number) {
        const column = parsedFields[index][0];
        const foundVal = mapping[column];
        if (foundVal) {
            return fieldsTypes.find((x) => x.id === parseInt(foundVal))?.id;
        } else {
            return "";
        }
    }
    //step 7
    async function showImportResponse(importTaskInfo: AxiosResponse<GetTaskProgressResponse, any>) {
        let title: string,
            aHref: string,
            aText: string,
            html: string | undefined = undefined,
            text: string,
            type: any,
            options: SweetAlertOptions;
        aHref = importTaskInfo.data.reportUrl;
        aText = t("employee.import.alert.error.reportbtn") as string;
        title = importTaskInfo.data.stopMessage;
        if (importTaskInfo.data.stopCode === ProgressStopCode.Error) {
            title = title ?? t("employee.import.alert.error.report");
            if (aHref) {
                html = `<div>
                <p style="color: #D20C0C" class="mt0 mb30">${title}</p>
                <div class="mb30"><a class="importError-download" target="_blank" href='${aHref}'>${aText}</a></div>
                </div>`;
                options = {
                    icon: "error",
                    html: html,
                    showCloseButton: false,
                    showCancelButton: true,
                    focusConfirm: false,
                    showConfirmButton: false,
                    cancelButtonText: "Cancel",
                };
            } else {
                html = `<div>
                <p>${title}</p>
                </div>`;
                options = {
                    html: html,
                    showCloseButton: true,
                    showConfirmButton: false,
                };
            }
            await swal.fire(options);
            goToStartNewImport();
            return;
        } else if (importTaskInfo.data.stopCode === ProgressStopCode.Success) {
            title = title ?? (t("employee.import.alert.confirm.text") as string);
            html = `<div><p>${title}</p></div>`;
            type = "info";
        } else if (importTaskInfo.data.stopCode === ProgressStopCode.Warning) {
            title = title ?? (t("employee.import.alert.confirm.warn.title") as string);
            html = `<div>
                <p style="color: #D20C0C" class="mt0 mb30">${title}</p>
                <div class="mb30"><a class="importError-download" href='${aHref}'>${aText}</a></div>
                <p style="color: #000000;">${t("employee.import.alert.warning.continue")}</p>
                </div>`;
            type = "warning";
        }
        const confirm = await swal.fire({
            icon: type,
            html: html,
            confirmButtonText: t("employee.import.alert.confirm.confirm") as string,
            cancelButtonText: t("employee.import.alert.confirm.cancel") as string,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showCancelButton: importTaskInfo.data.stopCode === ProgressStopCode.Warning ? true : false,
        });
        if (!confirm) {
            return;
        }
        if (!taskId) {
            return;
        }
        if (!confirm.isConfirmed) {
            goToStartNewImport();
            return;
        }

        // Start import
        swal.showLoading(null);
        var impStartApiResult = await api.importStart(taskId);
        if (impStartApiResult.data.errorMessage) {
            swal.fire({
                icon: "error",
                text: impStartApiResult.data.errorMessage,
            });
            return;
        }
        await getImportedFileStatus(taskId);
    }

    //step 8
    async function getImportedFileStatus(idTask: string) {
        const req = new GetTaskProgressRequest();
        req.taskId = idTask;
        req.taskStatus = QueueJobStatus.Import_Import_Progress;
        popupService.showLoading();
        const response = await api.queueImportProgress(req);
        if (response.data.error || response.data.stopMessage) {
            setUploadedFile(null);
            swal.fire({
                icon: "error",
                text: response.data.error ?? response.data.stopMessage,
            });
            return;
        }
        if (response.data.stopCode === ProgressStopCode.Success) {
            popupService.close();
            const importStatusApiResult = await api.getTaskInfo(idTask);
            if (importStatusApiResult.data.errorMessage) {
                swal.fire({
                    icon: "error",
                    text: response.data.error,
                });
                return;
            }
            showSuccess(importStatusApiResult.data);
            return;
        } else {
            setTimeout(async () => {
                await getImportedFileStatus(idTask);
            }, 1500);
        }
    }
    //step 9
    async function showSuccess(task: GetTaskInfoResponse) {
        var resultMSG = "";
        // if (lockedMapping || isOriginSelectedProfileMapping) {
        resultMSG = `<div> <p>${t("employee.import.complete.success.msg")}</p><p>${t(
            "employee.importSuccessMsg"
        )}</p> </div>`;
        if (task.isRefdataInserted) {
            resultMSG = `<div> <p>${t("employee.import.complete.success.msg")}</p>
                <p>${t("employee.import.alert.instruction.afterupdating")}</p> <p>${t(
                "employee.importSuccessMsg"
            )}</p> </div>`;
        }
        await swal.fire({
            icon: "success",
            html: resultMSG,
            allowOutsideClick: false,
            allowEscapeKey: false,
            confirmButtonText: t("employee.import.close") as string,
            showCloseButton: true,
        });
        // } else {
        //     resultMSG = `<div> <p>${t("import.profile.instruction.text")}</p> </div>`;
        //     if (task.isRefdataInserted) {
        //         resultMSG = `<div> <p>${t("import.profile.instruction.afterupdating")}</p> <p>${t(
        //             "import.profile.instruction.text"
        //         )}</p> </div>`;
        //     }
        //     await swal.fire({
        //         icon: "success",
        //         title: t("import.general.success.text") as string,
        //         input: "text",
        //         html: resultMSG,
        //         allowOutsideClick: false,
        //         allowEscapeKey: false,
        //         inputPlaceholder: t("import.placeholder.createprofile") as string,
        //         confirmButtonText: t("dashboard.modal.timeline.btn.save") as string,
        //         cancelButtonText: t("import.general.validate.confirm.cancel") as string,
        //         inputValidator: async (val) => {
        //             if (!val || val.length < 2) {
        //                 return t("import.profile.validation.error");
        //             }
        //             if (taskId) {
        //                 const req = new SaveProfileRequest();
        //                 req.name = val;
        //                 req.queueJobId = taskId;
        //                 const apiResult = await api.saveImportProfile(req);
        //                 if (apiResult.data.errorMessage) {
        //                     return apiResult.data.errorMessage;
        //                 }
        //             }
        //             return null;
        //         },
        //     });
        // }
        setMapping({});
        navigate(`/employees`);
    }
    async function createNewGroup() {
        if (!newGroupName) {
            swal.fire({
                icon: "error",
                text: t("employee.import.alert.setNewGroupName") as string,
            });
            return;
        }
        const request = new NewGroupRequest();
        request.Name = newGroupName;
        swal.showLoading(null);
        try {
            const response = await api.addNewGroup(request);
            if (response.data.error) {
                setUploadedFile(null);
                swal.fire({
                    icon: "error",
                    text: response.data.error,
                });
                return;
            }
            swal.close();
            setNewGroupId(response.data.id.toString());
        } catch (error) {
            swal.close();
            popupService.showApiError(error);
        }
    }
    const addNewGroupModal = openAddNewGroup ? (
        <div>
            <Modal
                open={openAddNewGroup}
                onClose={() => setOpenAddNewGroup(false)}
                sx={{ backdropFilter: "blur(6px)" }}
            >
                <div className="new-forms-modal-style add-group-modal-style">
                    <div className="new-forms-modal-header">
                        <CancelIcon
                            onClick={() => setOpenAddNewGroup(false)}
                            style={{ color: "#586A84", cursor: "pointer" }}
                        />
                    </div>
                    <div className="new-forms-modal-content add-group-modal-content">
                        <BaseInput
                            id="newGroupName-input"
                            label={t("employee.import.newEmployeeGroupTitle")}
                            type="search"
                            size="small"
                            value={newGroupName}
                            onChange={(event) => setNewGroupName(event.target.value)}
                            sx={{
                                width: "100%",
                                height: "40px",
                            }}
                        />
                    </div>
                    <div className="new-forms-modal-footer">
                        <BaseButton
                            style={{ padding: "0px 42px" }}
                            onClick={() => createNewGroup()}
                            className="cancel-button"
                        >
                            {t("button.create")}
                        </BaseButton>
                        <BaseButton
                            style={{ padding: "0px 42px" }}
                            onClick={() => setOpenAddNewGroup(false)}
                            className="cancel-button"
                        >
                            {t("button.cancel")}
                        </BaseButton>
                    </div>
                </div>
            </Modal>
        </div>
    ) : (
        ""
    );
    return (
        <div key="import-container" className="import-container">
            <div key="import_container_header" className="import_container_header">
                <div key="import_title" className="import_title">
                    {t("employee.import.importTitle")}
                </div>
                <div key="import_header_buttons" className="import_header_buttons">
                    <BaseButton key="basic-button1" onClick={() => changeStep(-1)} className="basic-button">
                        {t("general.Back")}
                    </BaseButton>
                    <div key="import_header_buttons2" style={{ width: "15px" }}></div>
                    <BaseButton
                        key="basic-button2"
                        onClick={() => changeStep(1)}
                        disabled={!nextBtnActive}
                        className={`basic-button next-button ${!nextBtnActive ? "next-btn-disabled" : ""}`}
                    >
                        {t("general.Next")}
                    </BaseButton>
                </div>
            </div>
            <div key="import_progressbar_wrapper" className="import_progressbar_wrapper">
                <div
                    key="import_progressStep_step1"
                    className={`import_progressStep import_progressStep_step1 ${currentStep === 1 ? "active" : ""}`}
                    onClick={() => changeStep(0)}
                >
                    <span key="step_circle1" className="step_circle">
                        1
                    </span>
                    <span key="step_title" className="step_title">
                        {t("employee.import.StepOneTitle")}
                    </span>
                    <span
                        style={{ lineHeight: "40px", marginInlineStart: "15px", display: "inline-block" }}
                        key="step_title_dots_2"
                        className="step_title"
                    >
                        ...
                    </span>
                </div>
                <div
                    key="import_progressStep_step2"
                    className={`import_progressStep import_progressStep_step1 ${currentStep === 2 ? "active" : ""}`}
                    onClick={() => changeStep(2)}
                >
                    <span key="step_circle" className="step_circle">
                        2
                    </span>
                    <span key="step_title2" className="step_title">
                        {t("employee.import.StepTwoTitle")}
                    </span>
                    <span
                        style={{ lineHeight: "40px", marginInlineStart: "15px", display: "inline-block" }}
                        key="step_title_dots_2"
                        className="step_title"
                    >
                        ...
                    </span>
                </div>
                <div
                    key="import_progressStep_step3"
                    className={`import_progressStep import_progressStep_step1 ${currentStep === 3 ? "active" : ""}`}
                    onClick={() => changeStep(3)}
                >
                    <span key="step_circle3" className="step_circle">
                        3
                    </span>
                    <span key="step_title_dots_3" className="step_title">
                        {t("employee.import.StepThreeTitle")}
                    </span>
                </div>
            </div>
            <div key="content_divider" className="content_divider"></div>
            <div key="import_content_wrapper" className="import_content_wrapper">
                {currentStep === 1 && (
                    <div key="on_step_one" className="on_step_one">
                        <div key="import_file_wrapper" className="import_file_wrapper">
                            <p key="import_file_title" className="import_file_title">
                                <i style={{ marginInlineEnd: "7px" }} className="icon-f icon-upload-icon fs-20" />
                                {t("employee.import.importFileTitle")}
                            </p>
                            <IconButton
                                className="import-info-button"
                                aria-owns={open ? "mouse-over-popover" : undefined}
                                aria-haspopup="true"
                                onMouseEnter={handlePopoverOpen}
                                onMouseLeave={handlePopoverClose}
                            >
                                <InfoIcon />
                            </IconButton>
                            <Popover
                                id="mouse-over-popover"
                                sx={{
                                    pointerEvents: "none",
                                }}
                                open={open}
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "center",
                                }}
                                transformOrigin={{
                                    vertical: "top",
                                    horizontal: "center",
                                }}
                                onClose={handlePopoverClose}
                                disableRestoreFocus
                            >
                                <Typography sx={{ p: 1 }} className="import-info-popover">
                                    {t("employee.import.helpDescription")}
                                </Typography>
                            </Popover>
                            <div key="import_file_content" className="import_file_content">
                                <BaseButton
                                    key="basic-button3"
                                    onClick={() => startImportFile()}
                                    className="basic-button"
                                >
                                    {t("employee.import.importFileBtn")}
                                </BaseButton>
                                <input
                                    key="uploadFile"
                                    onInput={(event) => uploadeFile(event)}
                                    style={{ display: "none" }}
                                    id="uploadFile"
                                    type="file"
                                    name="uploadFile"
                                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .csv"
                                />
                                <span key="upload_file_title" style={{ marginInlineStart: "15px" }}>
                                    {!uploadedFile ? (
                                        <span key="noFileChosen" className="no_file_chosen">
                                            {t("employee.import.noFileChosen")}
                                        </span>
                                    ) : (
                                        <span key="import_file_name" className="import_file_name">
                                            {uploadedFile}
                                        </span>
                                    )}
                                </span>
                            </div>
                            <p key="import_file_article" className="import_file_article">
                                {t("employee.import.importFileText.pleaseMakeSure")}
                            </p>
                            <p key="import_file_article2" className="import_file_article">
                                {t("employee.import.importFileText.inExcelFiles")}
                            </p>
                            <p key="import_file_article3" className="import_file_article">
                                <span>{t("employee.import.importFileText.youCanAlsoUse")}</span>
                                <a
                                    className="example_file_link"
                                    style={{ marginInlineStart: "5px" }}
                                    href={`https://lalundev.s3.eu-central-1.amazonaws.com/public/EmployeeImportTemplate-${authStore.cultureTwoLetter}-20230712.xlsx`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Excel
                                </a>
                                <span style={{ margin: "0 5px" }}>{t("employee.import.importFileText.or")}</span>
                                <a
                                    className="example_file_link"
                                    style={{ marginInlineEnd: "5px" }}
                                    href={`https://lalundev.s3.eu-central-1.amazonaws.com/public/EmployeeImportTemplate-${authStore.cultureTwoLetter}-20230712.csv`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    CSV
                                </a>
                                <span>{t("employee.import.importFileText.formats")}</span>
                            </p>
                        </div>
                    </div>
                )}
                {currentStep === 2 && (
                    <div key="on_step_two" className="on_step_two">
                        <div className="column_title">
                            <p className="import_file_article">{t("employee.import.selectColumnType")}</p>
                        </div>
                        <div className="step_two_columns_wrapper">
                            <div className="columns_selection">
                                <BaseInput
                                    select
                                    label={t("employee.import.uniqueColumnType")}
                                    size="small"
                                    sx={{
                                        width: "250px",
                                    }}
                                    value={uniqueColumnValue ?? ""}
                                    onChange={(event) => setUniqueColumnValue(event.target.value)}
                                >
                                    {uniqueKeyColumnTypes.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </BaseInput>
                            </div>
                        </div>

                        <p key="import_file_article4" className="import_file_article">
                            {t("employee.import.importFileText.makeTheColumns")}
                        </p>
                        <div key="step_two_columns_wrapper2" className="step_two_columns_wrapper">
                            <div key="column_title2" className="column_title">
                                <strong key="yourFields">{t("employee.import.importFileText.yourFields")}</strong>
                            </div>
                            <div key="columns_selection2" className="columns_selection">
                                <strong key="welloAppFields">
                                    {t("employee.import.importFileText.welloAppFields")}
                                </strong>
                            </div>
                        </div>
                        {parsedFields.map((field, index) => (
                            <div key={`step_two_columns_wrapper-${index}`} className="step_two_columns_wrapper">
                                <div key="column_title3" className="column_title">
                                    <p key="import_file_article3" className="import_file_article">
                                        <strong key="column_name" style={{ marginInlineEnd: "15px" }}>
                                            {field[0]}
                                        </strong>
                                        <span key="field_name">{field[1]}</span>
                                    </p>
                                </div>
                                <div key="columns_selection3" className="columns_selection">
                                    {/* value={filterEmployeesStatus}
                                onChange={(event) => setFilterEmployeesStatus(event.target.value)} */}
                                    {/* value={docToAdd.category ?? ""} */}
                                    <BaseInput
                                        key={`select-${index}`}
                                        select
                                        label={t("employee.import.importFileText.selectField")}
                                        size="small"
                                        sx={{
                                            width: "250px",
                                        }}
                                        onChange={(event: any) => fullFillDictionary(index, event.target.value)}
                                        value={getFieldValue(index)}
                                    >
                                        <MenuItem value="">None</MenuItem>
                                        {[...fieldsTypes]
                                            .sort((a, b) => a.text.localeCompare(b.text))
                                            .map((option) => (
                                                <MenuItem key={option.id} value={option.id}>
                                                    {option.text}
                                                </MenuItem>
                                            ))}
                                    </BaseInput>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {addNewGroupModal}
        </div>
    );
};
export default ImportEmployees;
